import {List, Set} from 'immutable';
import {IFilterFilter, IPromise} from 'angular';
import {IStation, IForecastModelDataInput, IStationDataValueType} from '../../api/api.types';
import {IStationApi} from '../../api/station-api.service';
import {IStationSelectorService} from './selector.service';
import {DoCheck, IChangeIndication} from '../../common/common.types';

export default class StationSelectorController {
  private static readonly $inject = [
    'stationApi',
    '$filter',
    '$element',
    'stationSelectorService',
    '$q',
    '$translate'
  ];
  private static readonly STORAGE_KEY = 'selector';

  isSelectable: boolean;
  isElevated: boolean;
  isDataTypeSelectingEnabled: boolean;
  isStationForecastActive: boolean;
  isListCached: boolean;
  forecastModelFrequency: string;

  stations: Array<IStation|IForecastModelDataInput>;
  /**
   * Search text for station selection.
   */
  searchText: string;
  /**
   * Selected station in the autocomplete dropdown.
   */
  selectedStation: IStation;
  /**
   * Stations selected.
   */
  selectedStations: Set<IStation>;
  /**
   * Id of the highlighted station.
   */
  highlightedStationId: number|null;
  /**
   * Array view of the selected stations (for ng-repeat).
   */
  selectedStationsArray: IStation[];
  /**
   * Indicates if meteostations should be included.
   */
  isWithMeteostations: boolean;
  /**
   * Callback to notify of changes in the set of selected stations.
   */
  onSelectChange: (callbackArgs: {
    selectedStations: Set<IStation>
  }) => void;
  /**
   * Callback to notify of a change in the highlighted station.
   */
  onStationHighlightChange: (callbackArgs: {
    station: IStation
  }) => void;

  /**
   * Promise that resolves to the array of available stations.
   */
  private stationsPromise: IPromise<IStation[]>;
  /**
   * AngularJS filter function.
   */
  private filter: IFilterFilter;

  private dischargeDataTypes = [{
    label: this.$translate.instant('DISCHARGE'),
    value: 'discharge'
  }, {
    label: this.$translate.instant('SNOW_DATA'),
    value: 'snow_data'
  }];

  private meteoDataTypes = [{
    label: this.$translate.instant('AIR_CELSIUS'),
    value: 'temperature'
  }, {
    label: this.$translate.instant('PRECIPITATION'),
    value: 'precipitation'
  }];

  constructor(private stationApi: IStationApi,
              $filter: ng.IFilterService,
              private $element: ng.IAugmentedJQuery,
              private stationSelectorService: IStationSelectorService,
              private $q: ng.IQService,
              private $translate:ng.translate.ITranslateService) {

    this.filter = $filter('filter');

    let stationPromises: IPromise<IStation[]>[] = [];

    const dischargeStationsPromise = stationApi.getDischargeStations();
    const meteoStationsPromise = stationApi.getMeteoStations();

    let stationsWithForecastResultPromise: IPromise<IStation[]>;

    if (this.isStationForecastActive) {
      stationsWithForecastResultPromise = stationApi.getDischargeStationsWithForecast();
      stationPromises.push(stationsWithForecastResultPromise);
    } else {
      stationPromises.push(dischargeStationsPromise);
    }
    
    if (typeof this.isSelectable === 'undefined') {
      this.isSelectable = true;
    }

    if (typeof this.isElevated === 'undefined') {
      this.isElevated = true;
    }

    if (typeof this.isListCached === 'undefined') {
      this.isListCached = true;
    }

    if (this.isWithMeteostations) {
      stationPromises.push(meteoStationsPromise)
    }

    this.stationsPromise = this.$q.all(stationPromises).then(([dischargeStations, meteoStations]: [IStation[], IStation[]]) => {
      let stations = dischargeStations;

      stations.forEach((station, index) => {
        if (this.stations && this.stations.length) {
          this.stations.forEach((stationFromInput) => {
            if (station.id === (<IForecastModelDataInput>stationFromInput).siteId || (<IStation>stationFromInput).id === station.id) {
              stations.splice(index, 1);
            }
          })
        }
      });

      if (meteoStations && meteoStations.length) {
        angular.forEach(meteoStations, (meteoStation) => {
          stations.push(meteoStation)
        });
      }

      if (this.isListCached) {
        this.selectedStations = this.stationSelectorService.getStoredStations(
            StationSelectorController.STORAGE_KEY, List(stations)); 
      }

      this.selectedStationsArray = this.selectedStations
          .toArray().sort(compareStations);

      if (!this.selectedStations.isEmpty()) {
        this.highlightedStationId = this.selectedStationsArray[0].id;
      }
      this.notifyChanges();
      return stations;
    });
  }

  $doCheck() {
    if (typeof this.stations !== 'undefined') {
      this.selectedStations = this.stations ? Set(this.stations) : Set([]);

      this.selectedStationsArray = this.selectedStations.toArray();
    }
  }

  $onChanges() {
    if (this.selectedStations && !this.selectedStations.find(
        station => station.id == this.highlightedStationId)) {
      this.highlightedStationId = null;
    }

    if (this.stations && this.stations.length && this.isDataTypeSelectingEnabled) {
      this.stations.forEach((stationFromInput) => {
        if (!stationFromInput.dataValueTypesSelectorHelper) {
          
          stationFromInput.dataValueTypesSelectorHelper = {
            discharge: null,
            snow_data: null,
            temperature: null,
            precipitation: null
          };

          stationFromInput.dataValueTypes.forEach((dataValueType) => {
            switch (dataValueType.type) {
              case 'discharge':
                stationFromInput.dataValueTypesSelectorHelper.discharge = {
                  checked: true,
                  laglength: dataValueType.laglength
                }
                break;
              case 'snow_data':
                stationFromInput.dataValueTypesSelectorHelper.snow_data = {
                  checked: true,
                  laglength: dataValueType.laglength
                }
                break;
              case 'temperature':
                stationFromInput.dataValueTypesSelectorHelper.temperature = {
                  checked: true,
                  laglength: dataValueType.laglength
                }
                break;
              case 'precipitation':
                stationFromInput.dataValueTypesSelectorHelper.precipitation = {
                  checked: true,
                  laglength: dataValueType.laglength
                }
                break;
              default:
                break;
            }
          })
        }
      })
    }
    
  }

  /**
   * Input element in the md-autocomplete component.
   */
  private get inputElement(): HTMLInputElement {
    return <HTMLInputElement>this.$element[0].querySelector('#station-input');
  }

  /**
   * Returns the stations matching a search text.
   */
  getMatchedStations(searchText: string) {
    return this.stationsPromise.then(stations => {
      return this.filter(stations, searchText).filter(station => {
        return !this.selectedStations.contains(station);
      });
    });
  }

  /**
   * Add the selected station to the set.
   */
  addToSelectedStations(station: IStation) {
    if (this.highlightedStationId == null) {
      this.highlightedStationId = station.id;
    }

    if (station.siteType === 'discharge' && this.isDataTypeSelectingEnabled) {
      station.dataValueTypesSelectorHelper = {
        discharge: {
          checked: true,
          laglength: this.forecastModelFrequency === 'seasonal' ? null : 1
        },
        snow_data: {
          checked: true,
          laglength: this.forecastModelFrequency === 'seasonal' ? null : 1
        }
      }
    }

    if (station.siteType === 'virtual-discharge' && this.isDataTypeSelectingEnabled) {
      station.dataValueTypesSelectorHelper = {
        discharge: {
          checked: true,
          laglength: this.forecastModelFrequency === 'seasonal' ? null : 1
        }
      }
    }

    if (station.siteType === 'meteo' && this.isDataTypeSelectingEnabled) {
      station.dataValueTypesSelectorHelper = {
        temperature: {
          checked: true,
          laglength: this.forecastModelFrequency === 'seasonal' ? null : 1
        },
        precipitation: {
          checked: true,
          laglength: this.forecastModelFrequency === 'seasonal' ? null : 1
        }
      }
    }
    
    this.setDataValueTypes(station);

    this.selectedStations = this.selectedStations.add(station);
    this.selectedStationsArray = this.selectedStations
        .toArray().sort(compareStations);
    this.searchText = '';
    this.inputElement && this.inputElement.blur();
    this.notifyChanges();
  }

  /**
   * Remove a selected station from the set.
   */
  removeFromSelectedStations(station: IStation) {
    this.selectedStations = this.selectedStations.remove(station);
    this.selectedStationsArray = this.selectedStations
        .toArray().sort(compareStations);
    if (station.id == this.highlightedStationId) {
      this.highlightedStationId = this.selectedStations.isEmpty()
          ? null
          : this.selectedStationsArray[0].id;
    }
    this.notifyChanges();
  }

  /**
   * Highlight a station in the list.
   */
  highlightStation(station: IStation) {
    this.highlightedStationId = station.id;
    this.notifyChanges();
  }

  onDataTypeToggle(station: IStation) {
    this.setDataValueTypes(station);
  }

  setDataValueTypes(station: IStation|IForecastModelDataInput) {
      station.dataValueTypes = [];
      angular.forEach(station.dataValueTypesSelectorHelper, (value, key) => {
        switch (key) {
          case ('discharge'):
            if (value && value.checked) {
              station.dataValueTypes.push({type: 'discharge', laglength: value.laglength})
            }
            break;
          case ('snow_data'):
            if (value && value.checked) {
              station.dataValueTypes.push({type: 'snow_data', laglength: value.laglength})
            }
            break;
          case ('temperature'):
            if (value && value.checked) {
              station.dataValueTypes.push({type: 'temperature', laglength: value.laglength})
            }
            break;
          case ('precipitation'):
            if (value && value.checked) {
              station.dataValueTypes.push({type: 'precipitation', laglength: value.laglength})
            }
            break;
          default:
            break;
        }
      });
  }

  isDataValueTypeInStationDataInputs(station: IStation, value: string) {
    let isPresent: boolean = false;
    station.dataValueTypes.forEach((dataValueType) => {
      if(dataValueType.type = value) {
        isPresent = true;
        return;
      }
    });
    return isPresent;
  }

  /**
   * Notify the callback about changes in the set of selected stations and
   * store it locally.
   */
  private notifyChanges() {
    if (this.isListCached) {
      this.stationSelectorService.storeStations(
          StationSelectorController.STORAGE_KEY,
          this.selectedStations);
    }
    
    if (this.onSelectChange) {
      this.onSelectChange({
        selectedStations: this.selectedStations
      });
    }
    if (this.onStationHighlightChange) {
      this.onStationHighlightChange({
        station: this.selectedStations.find(
            station => station.id == this.highlightedStationId)
      });
    }
  }
}

function compareStations(a: IStation, b: IStation):number {
  return a.siteCode.localeCompare(b.siteCode);
}



/** WEBPACK FOOTER **
 ** ./app/station/selector/selector.controller.ts
 **/